import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getCookie } from "../cookie_helper/cookie";

export const listenerApi = createApi({
  reducerPath: "listenerApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_URL,
    prepareHeaders: (headers) => {
      const token = getCookie("token");

      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    listenerList: builder.query({
      query: ({ page = 1, pageSize = 10, searchParams, fromDate,toDate, archived }) => ({
        url: `listener/listener-list`,
        method: "GET",
        params: { page, pageSize, searchParams, fromDate,toDate, archived },
      }),
    }),
    applications: builder.query({
      // view: "pending" (default) or "rejected" — rejected applications are
      // listed in their own tab instead of mixed into the queue.
      query: ({ page = 1, pageSize = 10, searchParams, date, view }) => ({
        url: `listener/listener-applications`,
        method: "GET",
        params: { page, pageSize, searchParams, date, view },
      }),
    }),
    profileApprovals: builder.query({
      query: ({ page = 1, pageSize = 10, searchParams, date, view }) => ({
        url: `listener/listener-profiles`,
        method: "GET",
        params: { page, pageSize, searchParams, date, view },
      }),
    }),
    listenerProfileFormLink: builder.mutation({
      query: (userId) => ({
        url: `listener/listener-profile-form-link`,
        method: "POST",
        body: { id: userId },
      }),
    }),

    // Mints a form link without emailing it, for pasting into WhatsApp/SMS.
    onboardingFormLink: builder.mutation({
      query: ({ id, formStep }) => ({
        url: `listener/onboarding-form-link`,
        method: "POST",
        body: { id, formStep },
      }),
    }),

    // Accepts either a bare userId (legacy callers) or { id, email }. The email
    // is optional and only needed for candidates who signed up on mobile and
    // therefore have no address on file — the backend saves whatever is passed.
    sendOnboardingForm1: builder.mutation({
      query: (arg) => ({
        url: `listener/send-onboarding-form-1`,
        method: "POST",
        body:
          typeof arg === "object" && arg !== null
            ? { id: arg.id, email: arg.email }
            : { id: arg },
      }),
    }),
    sendOnboardingForm2: builder.mutation({
      query: (arg) => ({
        url: `listener/send-onboarding-form-2`,
        method: "POST",
        body:
          typeof arg === "object" && arg !== null
            ? { id: arg.id, email: arg.email }
            : { id: arg },
      }),
    }),
    rejectRequest: builder.mutation({
      query: ({ userId, reason, text }) => ({
        url: `listener/reject-request`,
        method: "POST",
        body: { userId, reason, text },
      }),
    }),
    listenerProfileApproval: builder.mutation({
      query: (userId) => ({
        url: `listener/listener-request-approval`,
        method: "POST",
        body: { userId },
      }),
    }),
    listenerProfile: builder.query({
      query: (id) => ({
        url: `listener/listener-profile/${id}`,
        method: "GET",
      }),
    }),
    // Permanent photo gallery (admin-managed, shown on listener profile view).
    listenerGallery: builder.query({
      query: (listenerId) => ({
        url: `listener/gallery/${listenerId}`,
        method: "GET",
      }),
    }),
    addGalleryPhotos: builder.mutation({
      query: ({ listenerId, formData }) => ({
        url: `listener/gallery/${listenerId}`,
        method: "POST",
        body: formData,
      }),
    }),
    deleteGalleryPhoto: builder.mutation({
      query: (photoId) => ({
        url: `listener/gallery/${photoId}`,
        method: "DELETE",
      }),
    }),
    listenerDelete: builder.mutation({
      query: (id) => ({
        url: `user/hard-delete-user`,
        method: "DELETE",
        body: { id },
      }),
    }),
    listenerSoftDelete: builder.mutation({
      query: ({ id, status, mobile_number }) => ({
        url: `user/soft-delete-listener`,
        method: "PUT",
        body: { id, status, mobile_number },
      }),
    }),
    sessionList: builder.query({
      query: ({ page = 1, limit = 10, search, searchUser, searchListener, fromDate, toDate, ludo }) => ({
        url: `listener/sessions-list`,
        method: "GET",
        params: { page, limit, search, searchUser, searchListener, fromDate, toDate, ludo: ludo || undefined },
      }),
    }),
    giftList: builder.query({
      query: ({ page = 1, limit = 10, search }) => ({
        url: `listener/gifts-list`,
        method: "GET",
        params: { page, limit, search },
      }),
    }),
    rechargeList: builder.query({
      query: ({ page = 1, limit = 10, search, fromDate, toDate }) => ({
        url: `listener/recharges-list`,
        method: "GET",
        params: { page, limit, search, fromDate, toDate },
      }),
    }),
    payoutsList: builder.query({
      query: ({ page = 1, limit = 10, search, fromDate, toDate }) => ({
        url: `listener/payouts-list`,
        method: "GET",
        params: { page, limit, search, fromDate, toDate },
      }),
    }),
    revenue: builder.query({
      query: () => ({
        url: `listener/revenue`,
        method: "GET",
      }),
    }),
    editSalary: builder.mutation({
      query: ({ id, updates }) => ({
        url: `/listener/update-payout`,
        method: "PUT",
        body: { id, updates },
      }),
    }),
    getSinglePayout: builder.query({
      query: (id) => ({
        url: `/listener/single-payout?id=${id}`,
        method: "GET",
      }),
    }),
    paySalaryUser: builder.mutation({
      query: (details) => ({
        url: "/listener/salary-payout",
        method: "POST",
        body: details,
      }),
    }),
    paySalary: builder.mutation({
      query: ({ id, amount ,transaction_id}) => ({
        url: `/listener/pay-salary`, 
        method: "POST",
        body: { id, amount,transaction_id },
      }),
    }),
    createSession: builder.mutation({
      query: (data) => ({
        url: "/session/create-session",
        method: "POST",
        body: data,
      }),
    }),
    bulkUpdateCharges: builder.mutation({
      query: (charges) => ({
        url: "listener/bulk-update-charges",
        method: "POST",
        body: charges,
      }),
    }),
    dailySummary: builder.query({
      query: ({ fromDate, toDate } = {}) => ({
        url: "listener/daily-summary",
        method: "GET",
        params: { fromDate, toDate },
      }),
    }),
    // First-match bandit priority lever — see services/matchingService.js on
    // the backend. weight is clamped server-side to [-10, 10].
    updateListenerPriority: builder.mutation({
      query: ({ listenerId, weight }) => ({
        url: "listener/priority",
        method: "PATCH",
        body: { listenerId, weight },
      }),
    }),
  }),
});

export const {
  useListenerListQuery,
  useProfileApprovalsQuery,
  useApplicationsQuery,
  useListenerProfileFormLinkMutation,
  useOnboardingFormLinkMutation,
  useSendOnboardingForm1Mutation,
  useSendOnboardingForm2Mutation,
  useRejectRequestMutation,
  useListenerProfileApprovalMutation,
  useListenerProfileQuery,
  useListenerGalleryQuery,
  useAddGalleryPhotosMutation,
  useDeleteGalleryPhotoMutation,
  useListenerDeleteMutation,
  useListenerSoftDeleteMutation,
  useSessionListQuery,
  useGiftListQuery,
  useRechargeListQuery,
  useRevenueQuery,
  usePayoutsListQuery,
  useEditSalaryMutation,
  useGetSinglePayoutQuery,
  usePaySalaryUserMutation,
  usePaySalaryMutation,
  useCreateSessionMutation,
  useBulkUpdateChargesMutation,
  useDailySummaryQuery,
  useUpdateListenerPriorityMutation,
} = listenerApi;
